import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { apiRequest } from '@/lib/api/client';
import { routes } from '@/lib/routes';
import { AuthCard } from './components/AuthCard';
import { passwordChecklist } from './validation';
import type { TranslationKey } from '@/i18n';

interface InvitationPreview {
  status: string;
  email: string | null;
  academia_id: string | null;
}

const AcceptInvitationPage = () => {
  const { t, locale, setLocale } = useI18n();
  const { notify } = useToast();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const userId = params.get('user')?.trim() ?? '';
  const token = params.get('token')?.trim() ?? '';

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // Apply URL ?lang= once on mount. After that the user can freely switch language.
  useEffect(() => {
    const rawLang = params.get('lang') || params.get('locale');
    if (!rawLang) return;
    const normalized = rawLang.toLowerCase() === 'kk' ? 'kz' : rawLang.toLowerCase();
    if (normalized === 'kz' || normalized === 'ru' || normalized === 'en') {
      setLocale(normalized as 'kz' | 'ru' | 'en');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty deps — run once on mount only

  useEffect(() => {
    if (!userId || !token) {
      setPreview({ status: 'invalid', email: null, academia_id: null });
      return;
    }
    let cancelled = false;
    apiRequest<InvitationPreview>('/auth/invitation/preview', {
      query: { user_id: userId, token },
    })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        if (!cancelled) setPreview({ status: 'invalid', email: null, academia_id: null });
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId]);

  const checks = passwordChecklist(password, confirm);
  const ready = Object.values(checks).every(Boolean);
  const otherSession = Boolean(
    user && preview?.email && user.email.toLowerCase() !== preview.email.toLowerCase(),
  );
  const canSetPassword = preview?.status === 'pending' && !otherSession;

  const statusMessage = (): TranslationKey | null => {
    if (!preview || preview.status === 'pending') return null;
    if (preview.status === 'expired') return 'auth.invite.expired';
    return 'auth.invite.invalid';
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSetPassword || !ready) return;
    setIsPending(true);
    try {
      await apiRequest('/auth/invitation/accept', {
        method: 'POST',
        body: { user_id: userId, token, password, locale },
      });
      logout();
      notify(t('auth.invite.submit'), 'success');
      const identifier = preview?.email || preview?.academia_id || '';
      navigate(identifier ? `${routes.login}?email=${encodeURIComponent(identifier)}` : routes.login, {
        state: { email: identifier },
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : t('auth.invite.invalid'), 'error');
    } finally {
      setIsPending(false);
    }
  };

  const blocked = statusMessage();

  return (
    <AuthCard title={t('auth.invite.title')} description={t('auth.invite.text')} showTabs={false}>
      {preview?.email ? (
        <p className="auth-note">
          {preview.email}
          {preview.academia_id ? ` · ${preview.academia_id}` : ''}
        </p>
      ) : null}
      {otherSession ? (
        <div className="auth-note">
          <p>{t('auth.invite.otherSession', { email: preview?.email ?? '' })}</p>
          <button type="button" className="auth-secondary" onClick={() => logout()}>
            {t('auth.invite.switch')}
          </button>
        </div>
      ) : null}
      {blocked ? <p className="field-error">{t(blocked)}</p> : null}
      {canSetPassword ? (
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label htmlFor="invite-password">{t('auth.password')}</label>
            <input
              id="invite-password"
              type={showPassword ? 'text' : 'password'}
              maxLength={128}
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="invite-confirm">{t('auth.invite.confirm')}</label>
            <input
              id="invite-confirm"
              type={showPassword ? 'text' : 'password'}
              maxLength={128}
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="auth-link"
            onClick={() => setShowPassword((current) => !current)}
          >
            {showPassword ? t('auth.password.hide') : t('auth.password.show')}
          </button>
          <p className="auth-note">{t('auth.error.password')} {t('auth.password.rulesHint')}</p>
          <ul className="auth-note">
            <li>{checks.length ? '✓' : '·'} {t('auth.password.rule.length')}</li>
            <li>{checks.upper ? '✓' : '·'} {t('auth.password.rule.upper')}</li>
            <li>{checks.lower ? '✓' : '·'} {t('auth.password.rule.lower')}</li>
            <li>{checks.digit ? '✓' : '·'} {t('auth.password.rule.digit')}</li>
            <li>{checks.special ? '✓' : '·'} {t('auth.password.rule.special')}</li>
            <li>{checks.match ? '✓' : '·'} {t('auth.password.rule.match')}</li>
          </ul>
          <button type="submit" className="auth-primary" disabled={isPending || !ready}>
            {isPending ? t('common.loading') : t('auth.invite.submit')}
          </button>
        </form>
      ) : null}
    </AuthCard>
  );
};

export default AcceptInvitationPage;
